import { useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import Seo from "@/components/Seo";
import { ExternalLink, Check, ChevronDown, CheckCircle2, ArrowRight, Send } from "lucide-react";
import { toast } from "sonner";

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="jj-card overflow-hidden" data-testid={`program-faq-${q.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}>
      <button onClick={() => setOpen(!open)} className="w-full p-5 flex items-center justify-between text-left">
        <span className="font-medium text-jjblue">{q}</span>
        <ChevronDown className={`h-4 w-4 text-burgundy shrink-0 transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && <div className="px-5 pb-5 text-sm text-[var(--jj-text-secondary)] leading-relaxed">{a}</div>}
    </div>
  );
}

function InterestForm({ program, accent }) {
  const { user } = useAuth();
  const [name, setName] = useState(user?.name || "");
  const [email, setEmail] = useState(user?.email || "");
  const [phone, setPhone] = useState("");
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      toast.error("Please share your email so we can reach you.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/program-interest", {
        program,
        name: name.trim(),
        email: email.trim(),
        phone: phone.trim(),
        message: message.trim(),
      });
      setSent(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not send. Try again in a moment.");
    } finally {
      setBusy(false);
    }
  };

  if (sent) {
    return (
      <div className="jj-card p-8 text-center fade-up" data-testid={`program-interest-sent-${program}`}>
        <CheckCircle2 className="h-10 w-10 text-burgundy mx-auto" strokeWidth={1.4} />
        <h3 className="font-serif text-3xl text-jjblue mt-4">You're on the list.</h3>
        <p className="mt-3 text-[var(--jj-text-secondary)] leading-relaxed max-w-md mx-auto">
          We'll reach out as soon as the next cohort or event opens. In the meantime, dwell in the Word — He's already preparing the path.
        </p>
      </div>
    );
  }

  const ctaClass = accent === "gold" ? "btn-gold" : "btn-primary";

  return (
    <form onSubmit={submit} className="jj-card p-7 space-y-4" data-testid={`program-interest-form-${program}`}>
      <div>
        <div className="overline text-burgundy">Express your interest</div>
        <h3 className="font-serif text-2xl text-jjblue mt-1">Be first in line.</h3>
        <p className="mt-2 text-sm text-[var(--jj-text-secondary)]">
          We'll let you know when the next cohort, workshop, or devotional drops. No spam — promise.
        </p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-sm text-jjblue/80">Name</label>
          <input className="input-jj mt-1" value={name} onChange={(e) => setName(e.target.value)} maxLength={120} data-testid={`program-interest-name-${program}`} />
        </div>
        <div>
          <label className="text-sm text-jjblue/80">Email *</label>
          <input className="input-jj mt-1" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} maxLength={160} data-testid={`program-interest-email-${program}`} />
        </div>
      </div>
      <div>
        <label className="text-sm text-jjblue/80">Phone (optional)</label>
        <input className="input-jj mt-1" value={phone} onChange={(e) => setPhone(e.target.value)} maxLength={40} placeholder="555-555-1212" data-testid={`program-interest-phone-${program}`} />
      </div>
      <div>
        <label className="text-sm text-jjblue/80">Anything you want us to know? (optional)</label>
        <textarea rows={3} maxLength={2000} className="input-jj mt-1" value={message} onChange={(e) => setMessage(e.target.value)} data-testid={`program-interest-message-${program}`} />
      </div>
      <button disabled={busy} className={`${ctaClass} w-full inline-flex items-center justify-center gap-2`} data-testid={`program-interest-submit-${program}`}>
        <Send className="h-4 w-4" /> {busy ? "Sending…" : "Count me in"}
      </button>
    </form>
  );
}

/**
 * Shared layout for any native program hub. Driven by the data in
 * `/src/lib/programs.js`. Distinct visual accents via `accent` prop ('gold' | 'burgundy').
 */
export default function ProgramHub({ program }) {
  if (!program) return null;
  const accent = program.accent || "burgundy";
  const heroOverlay =
    accent === "gold"
      ? "bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7]/85 to-[#FDFBF7]/40"
      : "bg-gradient-to-t from-[#FDFBF7] via-[#FDFBF7]/80 to-[#FDFBF7]/30";
  const accentChipBg = accent === "gold" ? "bg-gold/15 text-burgundy" : "bg-burgundy/10 text-burgundy";
  const accentRing = accent === "gold" ? "ring-[var(--jj-gold)]/60" : "ring-[var(--jj-burgundy)]/40";

  return (
    <div className="bg-cream" data-testid={`program-${program.key}`}>
      <Seo
        title={`${program.name} · Jesus Just Us`}
        description={program.heroBlurb}
        path={`/${program.key}`}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          {program.heroImage && (
            <img src={program.heroImage} alt="" className="w-full h-full object-cover" />
          )}
          <div className={`absolute inset-0 ${heroOverlay}`} />
        </div>
        <div className="relative max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 pt-20 pb-24 lg:pt-24 lg:pb-32 fade-up">
          <div className="overline text-burgundy mb-5">{program.overline}</div>
          <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl text-jjblue leading-[1.04] tracking-tight max-w-3xl">
            {program.name}
          </h1>
          <p className="mt-5 font-serif text-2xl sm:text-3xl text-burgundy/90 italic max-w-3xl">
            {program.tagline}
          </p>
          <p className="mt-7 max-w-2xl text-lg text-[var(--jj-text-secondary)] leading-relaxed">
            {program.heroBlurb}
          </p>
          <div className="mt-9 flex items-center gap-3 flex-wrap">
            <a href="#interest" className={`${accent === "gold" ? "btn-gold" : "btn-primary"} inline-flex items-center gap-2`} data-testid={`program-${program.key}-cta-interest`}>
              <ArrowRight className="h-4 w-4" /> Express interest
            </a>
            {program.externalUrl && (
              <a
                href={program.externalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-secondary inline-flex items-center gap-2"
                data-testid={`program-${program.key}-cta-external`}
              >
                {program.externalLabel || "Open program site"} <ExternalLink className="h-4 w-4" />
              </a>
            )}
          </div>
        </div>
      </section>

      {/* Pillars */}
      <section className="max-w-6xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        <div className="text-center max-w-xl mx-auto mb-12">
          <div className="overline text-burgundy">The pillars</div>
          <h2 className="font-serif text-4xl text-jjblue mt-2 leading-tight">What we're building.</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {(program.pillars || []).map((p) => {
            const Icon = p.icon;
            return (
              <div key={p.title} className={`jj-card p-6 ring-1 ${accentRing}`} data-testid={`program-pillar-${p.title.replace(/\s+/g, "-").toLowerCase()}`}>
                <div className={`h-11 w-11 rounded-2xl ${accentChipBg} flex items-center justify-center`}>
                  <Icon className="h-5 w-5" strokeWidth={1.5} />
                </div>
                <h3 className="font-serif text-2xl text-jjblue mt-5 leading-tight">{p.title}</h3>
                <p className="mt-3 text-sm text-[var(--jj-text-secondary)] leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Modules */}
      <section className="bg-cream-surface border-y border-jj">
        <div className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
          <div className="text-center max-w-xl mx-auto mb-12">
            <div className="overline text-burgundy">The journey</div>
            <h2 className="font-serif text-4xl text-jjblue mt-2 leading-tight">The modules.</h2>
          </div>
          <ol className="space-y-4">
            {(program.modules || []).map((m, idx) => (
              <li key={m.num} className="jj-card bg-white p-6 sm:p-7 flex items-start gap-5" data-testid={`program-module-${m.num}`} style={{ animationDelay: `${idx * 40}ms` }}>
                <div className={`shrink-0 font-serif text-4xl ${accent === "gold" ? "text-gold" : "text-burgundy"} leading-none`}>{m.num}</div>
                <div>
                  <h3 className="font-serif text-2xl text-jjblue leading-tight">{m.title}</h3>
                  <p className="mt-2 text-[var(--jj-text-secondary)] leading-relaxed">{m.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* Who it's for */}
      <section className="max-w-5xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        <div className="grid lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-5">
            <div className="overline text-burgundy">Who it's for</div>
            <h2 className="font-serif text-4xl text-jjblue mt-2 leading-tight">Maybe this is your name being called.</h2>
            <p className="mt-5 text-[var(--jj-text-secondary)] leading-relaxed">
              You don't have to have it all figured out. You just have to be ready to take the next obedient step.
            </p>
          </div>
          <ul className="lg:col-span-7 space-y-3">
            {(program.whoFor || []).map((line) => (
              <li key={line} className="jj-card p-5 flex items-start gap-3" data-testid={`program-who-${line.slice(0, 20).replace(/\s+/g, "-").toLowerCase()}`}>
                <Check className={`h-4 w-4 mt-1 shrink-0 ${accent === "gold" ? "text-gold" : "text-burgundy"}`} strokeWidth={2} />
                <span className="text-jjblue/90">{line}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQs */}
      {(program.faqs || []).length > 0 && (
        <section className="bg-cream-surface border-y border-jj">
          <div className="max-w-3xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
            <div className="text-center mb-10">
              <div className="overline text-burgundy">Common questions</div>
              <h2 className="font-serif text-4xl text-jjblue mt-2 leading-tight">You might be wondering…</h2>
            </div>
            <div className="space-y-3">
              {program.faqs.map((f) => <FaqItem key={f.q} q={f.q} a={f.a} />)}
            </div>
          </div>
        </section>
      )}

      {/* Interest form */}
      <section id="interest" className="max-w-2xl mx-auto px-6 sm:px-8 lg:px-12 py-20">
        <InterestForm program={program.key} accent={accent} />
        {program.externalUrl && (
          <p className="mt-6 text-center text-sm text-[var(--jj-muted)]">
            Already a {program.name} member?{" "}
            <a href={program.externalUrl} target="_blank" rel="noopener noreferrer" className="text-burgundy font-medium hover:underline inline-flex items-center gap-1">
              Open the full program site <ExternalLink className="h-3 w-3" />
            </a>
          </p>
        )}
      </section>

      <section className="max-w-3xl mx-auto px-6 pb-20 text-center">
        <p className="text-sm text-[var(--jj-muted)]">
          Part of the <Link to="/family" className="text-burgundy font-medium hover:underline">Jesus Just Us family</Link>.
        </p>
      </section>
    </div>
  );
}
